/**
 * Platform admin support navigation for users across portals.
 */

import { getEnterAgencyPathFromSupportContext } from './agencyType';

const PLATFORM_ROLES = new Set([
  'platform_owner',
  'super_admin',
  'platform_admin',
  'platform_support',
  'platform_billing',
  'platform_viewer',
]);

const CLIENT_ROLES = new Set([
  'self_repair',
  'self_repair_customer',
  'self_repair_user',
  'premium_user',
  'managed_client',
]);

const AGENCY_STAFF_ROLES = new Set([
  'owner',
  'agency_owner',
  'agency_manager',
  'specialist',
  'staff',
  'agency_admin',
  'agency_staff',
]);

export function isTargetPlatformUser(user) {
  if (!user) return false;
  return Boolean(user.is_platform_admin) || PLATFORM_ROLES.has(String(user.role || '').toLowerCase());
}

export function isClientPortalUser(user) {
  if (!user) return false;
  const role = String(user.role || '').toLowerCase();
  const portal = String(user.portal_type || '').toLowerCase();
  return (
    CLIENT_ROLES.has(role) ||
    portal === 'self_serve_portal' ||
    portal === 'managed_client_portal'
  );
}

export function isAgencyStaffUser(user) {
  if (!user?.agency_id) return false;
  return AGENCY_STAFF_ROLES.has(String(user.role || '').toLowerCase());
}

export function supportDashboardPath(portalType, agencyId = null) {
  const portal = String(portalType || '').toLowerCase();
  if (portal === 'managed_client_portal') return '/client/dashboard';
  if (portal === 'self_repair' || portal === 'self_serve_portal') return '/dashboard';
  if (portal === 'fundgenius_hq' || portal === 'fundgenius') {
    if (agencyId) {
      return `/platform/fundgenius-hq/clients?agency_id=${encodeURIComponent(agencyId)}`;
    }
    return '/platform/fundgenius-hq/clients';
  }
  if (portal === 'agency' || portal === 'agency_staff' || portal === 'staff_portal') {
    if (agencyId) return `/agency/${agencyId}/dashboard`;
    return '/staff/dashboard';
  }
  return '/dashboard';
}

/**
 * Open the user's live profile in support/admin context.
 * Returns { action: 'edit' | 'navigated' } for callers that should open EditUserDrawer.
 */
export async function openUserSupportProfile(user, {
  navigate,
  enterClientSupport,
  enterAgencyContext,
  canUseSupportContext,
  currentUserId,
}) {
  if (!user?.user_id) {
    throw new Error('User id required');
  }

  if (isTargetPlatformUser(user)) {
    return { action: 'edit' };
  }

  if (isClientPortalUser(user)) {
    if (canUseSupportContext) {
      const result = await enterClientSupport(
        user.user_id,
        `Platform support: ${user.name || user.email}`
      );
      const path =
        (result?.redirect_path && String(result.redirect_path).startsWith('/')
          ? result.redirect_path
          : null)
        || (result?.redirect_to && String(result.redirect_to).startsWith('/')
          ? result.redirect_to
          : null)
        || supportDashboardPath(
          result?.portal_type
            || result?.effective_portal_type
            || user.portal_type
            || 'self_repair'
        );
      window.location.href = path;
      return { action: 'navigated' };
    }
    // Never deep-link Self Repair customers into Agency Settings via agency_id.
    return { action: 'edit' };
  }

  if (user.agency_id && isAgencyStaffUser(user)) {
    if (canUseSupportContext) {
      const result = await enterAgencyContext(
        user.agency_id,
        `Platform support: ${user.name || user.email}`
      );
      const path =
        (result?.redirect_path && String(result.redirect_path).startsWith('/')
          ? result.redirect_path
          : null)
        || (result?.redirect_to && String(result.redirect_to).startsWith('/')
          ? result.redirect_to
          : null)
        || getEnterAgencyPathFromSupportContext(result, {
          agency_id: user.agency_id,
          agency_type: user.agency_type || user.platform_type || user.type,
        });
      window.location.href = path;
      return { action: 'navigated' };
    }
    if (navigate) {
      navigate(`/platform/agencies/${user.agency_id}?tab=users&userId=${user.user_id}`);
      return { action: 'navigated' };
    }
  }

  if (user.agency_id && navigate && isAgencyStaffUser(user)) {
    navigate(`/platform/agencies/${user.agency_id}?tab=users&userId=${user.user_id}`);
    return { action: 'navigated' };
  }

  return { action: 'edit' };
}

export function truncateUserId(userId, max = 18) {
  if (!userId) return '—';
  if (userId.length <= max) return userId;
  return `${userId.slice(0, max)}…`;
}
